import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Project } from '../types';

interface QueryTableListProps {
  project: Project;
  onOpenQueryTable: (project: Project) => void;
}

const QueryTableList: React.FC<QueryTableListProps> = ({ project, onOpenQueryTable }) => {
  const [structuredQueries, setStructuredQueries] = useState<any[]>([]);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New loading state

  const loadStructuredQueries = useCallback(async () => {
    setIsLoading(true); // Start loading
    try {
      const response = await apiService.getStructuredQueries(project.id);
      setStructuredQueries(response.queries || []);
    } catch (error) {
      console.error('Error loading structured queries:', error);
      notificationService.error('Failed to load structured queries');
    } finally {
      setIsLoading(false); // End loading
    }
  }, [project.id, setStructuredQueries, setIsLoading]);

  const createInitialQuery = useCallback(() => {
    setShowCreateQueryModal(true);
  }, []);

  useEffect(() => {
    loadStructuredQueries();
  }, [project.id, loadStructuredQueries]);

  const handleCreateQuery = async (queryData: any) => {
    try {
      // Create structured query with the original query as the first column
      const response = await apiService.createStructuredQuery(project.id, {
        ...queryData,
        columns: [
          {
            name: 'Original Query',
            query: queryData.original_query,
            type: 'analysis'
          }
        ]
      });
      
      notificationService.success('Structured query created successfully!');
      setShowCreateQueryModal(false);
      
      // Reload structured queries
      await loadStructuredQueries();
      
      // Auto-run the original query for all files
      if (response.id) {
        setTimeout(async () => {
          try {
            await apiService.runStructuredQuery(response.id);
            notificationService.success('Running original query for all documents...');
          } catch (error) {
            console.error('Error auto-running query:', error);
          }
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error creating structured query:', error);
      notificationService.error('Failed to create structured query');
    }
  };

  // Show empty state if no structured queries exist
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>Loading query tables...</p>
      </div>
    );
  }

  if (structuredQueries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: '50%',
          width: '80px',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <i className="bi bi-table" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
        </div>
        <h4 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem 0', fontWeight: 600 }}>
          No Query Tables Yet
        </h4>
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>
          Create a structured query table to analyze multiple documents with column-based queries.
          <br />
          Perfect for contract reviews, due diligence, and comparative analysis.
        </p>
        <button
          onClick={createInitialQuery}
          style={{
            background: 'var(--text-primary)',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.75rem 1.5rem',
            color: 'var(--bg-color)',
            fontWeight: 500,
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
          Create Query Table
        </button>

        {/* Create Query Modal */}
        {showCreateQueryModal && (
          <CreateQueryModal
            onSubmit={handleCreateQuery}
            onCancel={() => setShowCreateQueryModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem' 
      }}>
        <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Query Tables</h5>
        <button
          onClick={createInitialQuery}
          style={{
            background: 'var(--text-primary)',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            color: 'var(--bg-color)',
            fontWeight: 500,
            fontSize: '0.85rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <i className="bi bi-plus"></i>
          New Query Table
        </button>
      </div>

      {/* Query Tables List */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {structuredQueries.map((query) => (
          <div 
            key={query.id}
            onClick={() => onOpenQueryTable(project)}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              padding: '1.25rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-hover)';
              e.currentTarget.style.borderColor = 'var(--border-hover)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.borderColor = 'var(--border-color)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '0.75rem' 
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <i className="bi bi-table" style={{ color: 'var(--primary-color)', fontSize: '1.1rem' }}></i>
                  <h6 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600, fontSize: '1rem' }}>
                    {query.name}
                  </h6>
                </div>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.85rem',
                  margin: 0,
                  lineHeight: 1.4
                }}>
                  {query.description || query.original_query}
                </p>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '1rem',
                marginLeft: '1rem'
              }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '0.25rem'
                  }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.9rem' }}>
                      {query.columns?.length || 0} columns
                    </span>
                  </div>
                  {query.last_run_at && (
                    <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                      Last run {new Date(query.last_run_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </small>
                  )}
                </div>
                <i className="bi bi-chevron-right" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}></i>
              </div>
            </div>
            
          </div>
        ))}
      </div>

      {/* Create Query Modal */}
      {showCreateQueryModal && (
        <CreateQueryModal 
          onSubmit={handleCreateQuery} 
          onCancel={() => setShowCreateQueryModal(false)} 
        />
      )}
    </div>
  );
};

// Create Query Modal Component
interface CreateQueryModalProps {
  onSubmit: (queryData: any) => void;
  onCancel: () => void;
}

const CreateQueryModal: React.FC<CreateQueryModalProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_query: '',
    query_type: 'analysis'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.original_query.trim()) {
      notificationService.warning('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        original_query: formData.original_query.trim(),
        query_type: formData.query_type,
        columns: []
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.75rem',
          width: '90%',
          maxWidth: '600px',
          padding: '2rem'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
            <i className="bi bi-table" style={{ marginRight: '0.75rem' }}></i>
            Create Query Table
          </h4>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Query Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Contract Review Analysis"
              required
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Original Query *
            </label>
            <textarea
              name="original_query"
              value={formData.original_query}
              onChange={handleInputChange}
              placeholder="e.g., Identify the parties, change of control provisions, termination, and red flags generally"
              rows={3}
              required
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none'
              }}
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              This will be the main query that gets broken down into column sub-queries
            </small>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 500 }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description of what this query table will analyze"
              rows={2}
              style={{
                width: '100%',
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'var(--bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim() || !formData.original_query.trim()}
              style={{
                background: 'var(--text-primary)',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1.5rem',
                color: 'var(--bg-color)',
                cursor: 'pointer',
                fontWeight: 500,
                opacity: (!formData.name.trim() || !formData.original_query.trim()) ? 0.6 : 1
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }}></div>
                  Creating...
                </>
              ) : (
                <>
                  <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
                  Create Query Table
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QueryTableList;
